import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { setDarkMode } from "@/store/customizer/CustomizerSlice";
import { setCookie, getCookie, deleteCookie } from "cookies-next";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import LightModeIcon from "@mui/icons-material/LightMode";
import ContrastIcon from "@mui/icons-material/Contrast";

const ThemeButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  transition: "all 0.3s ease",
  color: theme.palette.grey[500],
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.grey[600],
  },
}));

const ThemeToggle = () => {
  const dispatch = useDispatch();
  const customizer = useSelector((state) => state.customizer);
  const [themeState, setThemeState] = useState("auto");
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const oldThemeCookie = getCookie("theme");
    if (oldThemeCookie) {
      deleteCookie("theme");
    }

    const userPreference = getCookie("theme-preference");

    if (!userPreference) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const lightQuery = window.matchMedia("(prefers-color-scheme: light)");

      const prefersDark = mediaQuery.matches;
      const systemTheme = prefersDark ? "dark" : "light";

      if (customizer.activeMode !== systemTheme) {
        dispatch(setDarkMode(systemTheme));
      }

      setThemeState("auto");
    } else {
      dispatch(setDarkMode(userPreference));
      setThemeState(userPreference);
    }

    setIsInitialized(true);

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      const userPreference = getCookie("theme-preference");

      if (!userPreference) {
        const newTheme = e.matches ? "dark" : "light";
        dispatch(setDarkMode(newTheme));
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [dispatch]);

  const handleToggle = () => {
    const userPreference = getCookie("theme-preference");

    if (!userPreference) {
      dispatch(setDarkMode("light"));
      setCookie("theme-preference", "light", {
        maxAge: 60 * 60 * 24 * 365,
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax",
        path: "/",
      });
      setThemeState("light");
    } else if (userPreference === "light") {
      dispatch(setDarkMode("dark"));
      setCookie("theme-preference", "dark", {
        maxAge: 60 * 60 * 24 * 365,
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax",
        path: "/",
      });
      setThemeState("dark");
    } else {
      deleteCookie("theme-preference", { path: "/" });
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      const systemTheme = prefersDark ? "dark" : "light";
      dispatch(setDarkMode(systemTheme));
      setThemeState("auto");
    }
  };

  const getIcon = () => {
    if (themeState === "auto") {
      return <ContrastIcon sx={{ fontSize: 28 }} />;
    } else if (themeState === "light") {
      return <BedtimeIcon sx={{ fontSize: 28 }} />;
    } else {
      return <LightModeIcon sx={{ fontSize: 28 }} />;
    }
  };

  const getTooltipText = () => {
    if (themeState === "auto") {
      return "Auto Theme";
    } else if (themeState === "light") {
      return "Dark Theme";
    } else {
      return "Light Theme";
    }
  };

  return (
    <Tooltip title={getTooltipText()} arrow placement="bottom">
      <ThemeButton aria-label="Theme toggle" onClick={handleToggle}>
        {getIcon()}
      </ThemeButton>
    </Tooltip>
  );
};

export default ThemeToggle;
